/**
 * Main Application Entry Point
 * Imports all modules and orchestrates the application
 */

// Core imports
import { AppState } from './core/state.js';
import { debounce, escapeHtml } from './core/utils.js';
import { startPoller } from './core/poller.js';

// Component imports
import { showNotification } from './components/notifications.js';
import { setupEscapeHandler } from './components/modal.js';

// Module imports
import { loadDashboard, loadStats, loadWorkerDashboard, loadRecentActivity } from './modules/dashboard.js';
import {
    loadHealthDashboard,
    refreshHealthDashboard,
    startHealthMonitoring,
    stopHealthMonitoring,
    loadServicesHealth,
    refreshServicesHealth,
    startServicesHealthCheck,
    stopServicesHealthCheck,
    initHeaderHealthIndicator
} from './modules/health.js';
import {
    startWorkerStatusCheck,
    loadWorkerDetails,
    toggleDownloadWorker,
    toggleProcessWorker,
    toggleDiarizationWorker,
    toggleAnalyticsWorker,
    toggleTranscriptionFetchWorker,
    resetStuckEpisodes,
    resetStuckLLM,
    loadLLMIssues,
    retryEpisodeLLM,
    retryAllFailedLLM,
    loadTranscriptionIssues,
    retryEpisodeTranscription,
    retryAllFailedTranscriptions
} from './modules/workers.js';
import { loadFeeds, reloadFeeds, toggleFeedAutoDownload, loadLLMSettings } from './modules/feeds.js';
import { loadDownloads, annotateEpisode, verifyDownload, stopDownloadsRefresh } from './modules/downloads.js';
import { fetchTranscription, fetchAllTranscriptions } from './modules/transcription.js';
import { checkAndAddDiarization, redoDiarization, viewMissingDiarization, refetchAllDiarization, refetchBoth } from './modules/diarization.js';
import { loadPodcasts, fetchPodcastHistory, handlePodcastsPageFromURL } from './modules/library.js';
import { loadEpisodes, applyEpisodeFilters, clearEpisodeFilters, downloadEpisode, processEpisode, filterByTag, addFilterTag, removeFilterTag, handleEpisodePageFromURL, navigateEpisodePage, togglePlayedStatus } from './modules/episodes.js';
import { viewEpisodeDetails } from './modules/episode-detail.js';
import { openEpisodeView, closeEpisodeView, seekToTime as episodeViewSeekToTime, searchInTranscription, clearTranscriptionSearch, navigateMatch, setEpisodeSearchQuery, toggleSection, changeTranscriptFont, addTag, removeTag, showTagSuggestions, hideSuggestions, selectTagSuggestion, handleTagInput } from './modules/episode-view.js';
import { performSearch, initSearch, handleSearchFromURL, openEpisodeWithSearch } from './modules/search.js';
import { seekToTime, toggleTranscriptionFormat, copyTranscription, initAudioPlayer, formatTime } from './modules/audio-player.js';
import { showPodcastVisualizations, showEpisodeWordCloud, showTagExplorer } from './modules/visualizations.js';
import { loadBatches, stopBatchPolling } from './modules/batches.js';

// URL-based routing for episode views
function handleRouteChange() {
    const hash = window.location.hash;

    if (hash.startsWith('#/episode/')) {
        const episodeId = parseInt(hash.split('/')[2]);
        if (!isNaN(episodeId)) {
            openEpisodeView(episodeId);
        }
    } else if (hash.startsWith('#/podcast/') && hash.includes('/episodes')) {
        // Episode list pagination: #/podcast/{id}/episodes?page=2
        switchTab('library');
        handleEpisodePageFromURL(hash);
    } else if (hash.startsWith('#/search')) {
        // Switch to search tab and handle search params
        switchTab('search');
        // handleSearchFromURL is called by initSearch when tab loads
    } else if (hash === '#/dashboard') {
        switchTab('dashboard');
    } else if (hash === '#/podcasts' || hash.startsWith('#/podcasts?')) {
        switchTab('podcasts');
        if (hash.includes('?')) {
            handlePodcastsPageFromURL(hash);
        }
    } else if (hash === '#/downloads') {
        switchTab('downloads');
    } else if (hash === '#/feeds') {
        switchTab('feeds');
    } else if (hash === '#/workers') {
        switchTab('workers');
    } else if (hash === '#/health') {
        switchTab('health');
    } else if (hash === '#/add') {
        switchTab('add-content');
    } else {
        // If we're on a non-episode route, close episode view if open
        const episodeView = document.getElementById('episode-detail-view');
        if (episodeView && episodeView.style.display !== 'none') {
            closeEpisodeView();
        }
    }
}

// Navigate back to home (main page)
function goHome(event) {
    if (event) {
        event.preventDefault();
    }

    // Close episode view if open
    const episodeView = document.getElementById('episode-detail-view');
    if (episodeView && episodeView.style.display !== 'none') {
        closeEpisodeView();
    }

    // Clear URL hash
    if (window.location.hash) {
        history.pushState(null, '', window.location.pathname);
    }

    switchTab('podcasts');
}

// Sidebar drawer (mobile)
function toggleSidebar() {
    const sidebar = document.getElementById('app-sidebar');
    const backdrop = document.getElementById('sidebar-backdrop');
    if (!sidebar) return;
    const open = sidebar.classList.toggle('open');
    if (backdrop) backdrop.classList.toggle('active', open);
}

function closeSidebar() {
    const sidebar = document.getElementById('app-sidebar');
    const backdrop = document.getElementById('sidebar-backdrop');
    if (sidebar) sidebar.classList.remove('open');
    if (backdrop) backdrop.classList.remove('active');
}

// Return to the currently playing episode
function returnToEpisode(event) {
    // Don't trigger when clicking control buttons or progress bar
    if (event && (event.target.closest('button') || event.target.closest('.progress-container'))) return;

    const episodeId = window.playingEpisodeId;
    if (episodeId) {
        openEpisodeView(episodeId);
    }
}

// Smart seek that works with both modal and full-page view
function smartSeekToTime(seconds) {
    // Try global audio first (full-page view)
    const globalAudio = document.getElementById('global-audio');
    if (globalAudio && globalAudio.src) {
        episodeViewSeekToTime(seconds);
        return;
    }
    // Fall back to modal audio
    seekToTime(seconds);
}

// Tab switching
function switchTab(tabName) {
    AppState.currentTab = tabName;

    const urlPatterns = {
        'dashboard': '#/dashboard',
        'podcasts': '#/podcasts',
        'downloads': '#/downloads',
        'feeds': '#/feeds',
        'workers': '#/workers',
        'health': '#/health',
        'search': '#/search',
        'add-content': '#/add'
    };

    const newUrl = urlPatterns[tabName] || `#/${tabName}`;
    if (window.location.hash !== newUrl) {
        history.pushState({ tab: tabName }, '', newUrl);
    }

    // Update sidebar active state
    document.querySelectorAll('.sidebar-link').forEach(link => {
        link.classList.toggle('active', link.dataset.tab === tabName);
    });

    // Close mobile drawer after navigation
    closeSidebar();

    document.querySelectorAll('.tab-content').forEach(content => content.classList.remove('active'));

    const tabContent = document.getElementById(tabName);
    if (tabContent) {
        tabContent.classList.add('active');
    }

    // Stop intervals when leaving tabs
    if (tabName !== 'dashboard') {
        stopServicesHealthCheck();
        stopBatchPolling();
    }
    if (tabName !== 'health') {
        stopHealthMonitoring();
    }
    if (tabName !== 'downloads') {
        stopDownloadsRefresh();
    }

    // Load tab-specific content
    switch(tabName) {
        case 'dashboard':
            loadDashboard();
            break;
        case 'podcasts':
            loadPodcasts();
            break;
        case 'downloads':
            loadDownloads();
            break;
        case 'feeds':
            loadFeeds();
            loadLLMSettings();
            break;
        case 'workers':
            loadWorkerDetails();
            break;
        case 'health':
            startHealthMonitoring();
            break;
        case 'search':
            initSearch();
            break;
    }
}

// Add podcast form handler
async function addPodcast(e) {
    e.preventDefault();

    const feedUrl = document.getElementById('feed-url').value;
    const button = e.target.querySelector('button');

    button.disabled = true;
    button.innerHTML = '<span class="loading"></span> Adding...';

    try {
        const formData = new FormData();
        formData.append('feed_url', feedUrl);

        const response = await fetch('/api/podcasts/add', {
            method: 'POST',
            body: formData
        });

        const result = await response.json();

        if (response.ok) {
            showNotification(`Added "${result.title}" with ${result.episode_count} episodes!`, 'success');
            document.getElementById('feed-url').value = '';
            loadPodcasts();
        } else {
            showNotification(result.detail || 'Failed to add podcast', 'error');
        }
    } catch (error) {
        console.error('Error adding podcast:', error);
        showNotification('Error adding podcast', 'error');
    } finally {
        button.disabled = false;
        button.innerHTML = 'Add Podcast';
    }
}

// Upload file form handler
async function uploadFile(e) {
    e.preventDefault();

    const fileInput = document.getElementById('audio-file');
    const button = e.target.querySelector('button');

    if (!fileInput.files.length) {
        showNotification('Please select a file', 'error');
        return;
    }

    button.disabled = true;
    button.innerHTML = '<span class="loading"></span> Uploading...';

    try {
        const formData = new FormData();
        formData.append('file', fileInput.files[0]);

        const response = await fetch('/api/upload', {
            method: 'POST',
            body: formData
        });

        const result = await response.json();

        if (response.ok) {
            showNotification('File uploaded successfully!', 'success');
            fileInput.value = '';
        } else {
            showNotification(result.detail || 'Failed to upload file', 'error');
        }
    } catch (error) {
        console.error('Error uploading file:', error);
        showNotification('Error uploading file', 'error');
    } finally {
        button.disabled = false;
        button.innerHTML = 'Upload';
    }
}

// Initialize application
function initApp() {
    console.log('Initializing Podcast App with ES6 modules...');

    // Setup URL-based routing
    window.addEventListener('hashchange', handleRouteChange);
    window.addEventListener('popstate', handleRouteChange);

    // Check for initial route, otherwise default to podcasts
    if (window.location.hash && window.location.hash.startsWith('#/')) {
        handleRouteChange();
        if (!window.location.hash.startsWith('#/episode/')) {
            const tab = window.location.hash.slice(2).split('?')[0].split('/')[0];
            const valid = ['podcasts', 'search', 'add', 'dashboard', 'downloads', 'workers', 'health', 'feeds'];
            if (!valid.includes(tab)) switchTab('podcasts');
        }
    } else {
        switchTab('podcasts');
    }

    // Start background processes
    startWorkerStatusCheck();
    initHeaderHealthIndicator();

    // Poll stats every 15s with backoff on failure (server caches for 30s anyway)
    startPoller('stats', loadStats, { baseMs: 15000, maxMs: 120000 });

    // Set up form event listeners
    const addPodcastForm = document.getElementById('add-podcast-form');
    const uploadForm = document.getElementById('upload-form');
    const searchInput = document.getElementById('search-input');

    if (addPodcastForm) {
        addPodcastForm.addEventListener('submit', addPodcast);
    }
    if (uploadForm) {
        uploadForm.addEventListener('submit', uploadFile);
    }
    if (searchInput) {
        searchInput.addEventListener('input', debounce(performSearch, 300));
    }

    // Setup modal escape handler
    setupEscapeHandler();

    // Initialize audio player keyboard shortcuts
    initAudioPlayer();

    console.log('Podcast App initialized successfully!');
}

// Expose functions globally for onclick handlers in HTML
// This is necessary during the transition period while we still have onclick attributes
window.switchTab = switchTab;
window.goHome = goHome;
window.toggleSidebar = toggleSidebar;
window.closeSidebar = closeSidebar;
window.returnToEpisode = returnToEpisode;
window.loadStats = loadStats;
window.loadDashboard = loadDashboard;
window.loadPodcasts = loadPodcasts;
window.loadEpisodes = loadEpisodes;
window.navigateEpisodePage = navigateEpisodePage;
window.loadDownloads = loadDownloads;
window.loadFeeds = loadFeeds;
window.loadWorkerDetails = loadWorkerDetails;
window.loadHealthDashboard = loadHealthDashboard;

// Episode actions
window.viewEpisodeDetails = viewEpisodeDetails;
window.viewEpisode = viewEpisodeDetails;
window.downloadEpisode = downloadEpisode;
window.processEpisode = processEpisode;
window.togglePlayedStatus = togglePlayedStatus;
window.annotateEpisode = annotateEpisode;
window.verifyDownload = verifyDownload;

// Transcription actions
window.fetchTranscription = fetchTranscription;
window.fetchAllTranscriptions = fetchAllTranscriptions;

// Diarization actions
window.checkAndAddDiarization = checkAndAddDiarization;
window.redoDiarization = redoDiarization;
window.viewMissingDiarization = viewMissingDiarization;
window.refetchAllDiarization = refetchAllDiarization;
window.refetchBoth = refetchBoth;

// Audio player
window.seekToTime = smartSeekToTime;  // Use smart seek that works with both views
window.toggleTranscriptionFormat = toggleTranscriptionFormat;
window.copyTranscription = copyTranscription;
window.formatTime = formatTime;

// Episode view (full-page)
window.openEpisodeView = openEpisodeView;
window.closeEpisodeView = closeEpisodeView;
window.searchInTranscription = searchInTranscription;
window.clearTranscriptionSearch = clearTranscriptionSearch;
window.navigateMatch = navigateMatch;
window.setEpisodeSearchQuery = setEpisodeSearchQuery;
window.toggleSection = toggleSection;
window.changeTranscriptFont = changeTranscriptFont;

// Tags
window.addTag = addTag;
window.removeTag = removeTag;
window.showTagSuggestions = showTagSuggestions;
window.hideSuggestions = hideSuggestions;
window.selectTagSuggestion = selectTagSuggestion;
window.handleTagInput = handleTagInput;

// Library
window.fetchPodcastHistory = fetchPodcastHistory;

// Episodes filtering
window.applyEpisodeFilters = applyEpisodeFilters;
window.clearEpisodeFilters = clearEpisodeFilters;
window.filterByTag = filterByTag;
window.addFilterTag = addFilterTag;
window.removeFilterTag = removeFilterTag;

// Workers
window.toggleDownloadWorker = toggleDownloadWorker;
window.toggleProcessWorker = toggleProcessWorker;
window.toggleDiarizationWorker = toggleDiarizationWorker;
window.toggleAnalyticsWorker = toggleAnalyticsWorker;
window.toggleTranscriptionFetchWorker = toggleTranscriptionFetchWorker;
window.resetStuckEpisodes = resetStuckEpisodes;
window.resetStuckLLM = resetStuckLLM;
window.loadLLMIssues = loadLLMIssues;
window.retryEpisodeLLM = retryEpisodeLLM;
window.retryAllFailedLLM = retryAllFailedLLM;
window.loadTranscriptionIssues = loadTranscriptionIssues;
window.retryEpisodeTranscription = retryEpisodeTranscription;
window.retryAllFailedTranscriptions = retryAllFailedTranscriptions;

// Feeds
window.reloadFeeds = reloadFeeds;
window.toggleFeedAutoDownload = toggleFeedAutoDownload;

// Health
window.refreshHealthDashboard = refreshHealthDashboard;
window.refreshServicesHealth = refreshServicesHealth;
window.startHealthMonitoring = startHealthMonitoring;
window.stopHealthMonitoring = stopHealthMonitoring;

// Search
window.performSearch = performSearch;
window.openEpisodeWithSearch = openEpisodeWithSearch;

// Visualizations
window.showPodcastVisualizations = showPodcastVisualizations;
window.showEpisodeWordCloud = showEpisodeWordCloud;
window.showTagExplorer = showTagExplorer;

// Notifications
window.showNotification = showNotification;

// State access
window.currentPodcastPage = 0;
window.currentPodcastId = null;
window.currentPodcastTitle = null;

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initApp);
} else {
    initApp();
}

// Export for potential future use
export {
    initApp,
    switchTab,
    AppState
};
